/**
 * Versions of an asset. 1.2.2: "Upload… → A new version of this asset" (any asset with media, not only graphics). The
 * asset's metadata, class, collections and shares belong to the asset and stay; the masters belong to their version,
 * so the further masters (Textless, M&E, …) of the version before stay with it (they match the old edit) and are added
 * again for the new one. 1.2.3: old versions: the Versions tab, Make current for an earlier version, deleting an earlier version to the recycle bin (when
 * nothing uses it), the Old versions scan and its cleanup job.
 */
import { z } from 'zod';

/**
 * POST /api/v1/assets/:id/versions/uploads: start a resumable upload that becomes version expectedVersion + 1 and the
 * current version. Its chunks and completion are any upload's; completing answers 409 VERSION_CONFLICT
 * {currentVersion, savedBy, savedAt} when another version arrived meanwhile, 422 KIND_MISMATCH for another media kind.
 */
export const VersionUploadBody = z.object({
  filename: z.string().min(1).max(500),
  sizeBytes: z.number().int().positive(),
  /** The version the uploader saw as current. */
  expectedVersion: z.number().int().min(1),
});
export type VersionUploadBody = z.infer<typeof VersionUploadBody>;

/** Error codes of a version upload. */
export const VERSION_ERRORS = {
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  KIND_MISMATCH: 'KIND_MISMATCH',
} as const;

/** The media kind a file name stands for (video, audio or image), as a version upload compares it; null for anything else. */
export function fileMediaKind(mimeType: string | null | undefined): 'video' | 'audio' | 'image' | null {
  if (!mimeType) return null;
  if (mimeType.startsWith('video/') || mimeType === 'application/mxf') return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('image/')) return 'image';
  return null;
}

/**
 * GET /api/v1/assets/:id/masters `previous` (1.2.2): the version before the current one and the variants it has masters
 * of that the current version has none of ("Version 3 had Textless and M&E; add them for version 4"). Null when there is
 * no earlier version, or nothing is missing.
 */
export interface PreviousVersionMasters {
  versionNumber: number;
  currentNumber: number;
  missing: Array<{ id: string; name: string; code: string }>;
}

/* ---------------------------------------------------------------- 1.2.3: old versions */

/**
 * What keeps an earlier version from being deleted (catalog/version-uses.ts versionUses, one list used by the
 * Versions tab, the delete and the Old versions scan and job). Graphics keep every version: PRISM Graphics and the
 * sequences' graphics tracks use them by number.
 */
export const VERSION_USE_KINDS = ['sequence', 'sequence_version', 'multicam', 'export', 'handoff', 'approval', 'job', 'graphic'] as const;
export type VersionUseKind = (typeof VERSION_USE_KINDS)[number];
export interface VersionUse {
  kind: VersionUseKind;
  /** What uses it, e.g. the sequence's name, “Export to ProRes”, “Ingest”. */
  name: string;
  /** Where to look at it in the app; null when there is no page for it. */
  link: string | null;
}

/** The reason, as the Versions tab, the delete's refusal and the scan's skipped list say it. */
export const VERSION_USE_LABELS: Record<VersionUseKind, string> = {
  sequence: 'A clip in a sequence',
  sequence_version: 'A clip in a saved version of a sequence',
  multicam: 'A multicam angle in a sequence',
  export: 'An export that is kept',
  handoff: 'An open Send to edit hand-off',
  approval: 'A pending approval of this version',
  job: 'Work in progress on it',
  graphic: 'A version of a graphic',
};

/** “A clip in a sequence: “Evening news”” */
export function describeVersionUse(u: VersionUse): string {
  return u.kind === 'graphic' ? VERSION_USE_LABELS.graphic : `${VERSION_USE_LABELS[u.kind]}: “${u.name}”`;
}

/** Where a version stands. */
export type VersionState = 'current' | 'earlier' | 'deleted' | 'purged';

/** GET /api/v1/assets/:id/versions: every version, newest first. */
export interface VersionDTO {
  id: string;
  number: number;
  state: VersionState;
  createdAt: string;
  createdBy: { id: string; displayName: string } | null;
  originalFilename: string | null;
  mediaKind: string | null;
  status: string;
  /** Its masters, the primary first: the variant and format (none after a purge). */
  masters: Array<{ id: string; label: string; variant: string; primary: boolean }>;
  /** All its files, one copy each (original, masters, proxies and previews), and every copy together. */
  sizeBytes: number;
  storedBytes: number;
  /** Where its files are: each location with the bytes there, and whether PRISM manages them there (deletes them on purge). */
  locations: Array<{ id: string; name: string; bytes: number; files: number; managed: boolean }>;
  /** In the bin: when, by whom, and when it is purged (with why it waits, when it does). */
  deletedAt: string | null;
  deletedBy: string | null;
  purgeAfter: string | null;
  purgeError: string | null;
  purgedAt: string | null;
  /** What uses it (earlier versions; the current one is never deleted). */
  uses: VersionUse[];
  /** The version's proxy and poster, signed, to play it read-only; null without one. */
  proxyUrl: string | null;
  posterUrl: string | null;
  /** The current version only: who made it current again with Make current, and when (from the audit); null otherwise. */
  madeCurrent?: { by: string | null; at: string } | null;
}

/**
 * POST /api/v1/assets/:id/versions/:number/make-current (1.2.3): an earlier version becomes the current one again.
 * Nothing is copied or deleted; the version that was current stays as an earlier version, and the next upload still
 * takes the next number never used. 409 VERSION_CONFLICT when the current version isn't `expectedCurrent`.
 */
export const MakeCurrentBody = z.object({
  /** The version the person saw as current. */
  expectedCurrent: z.number().int().min(1),
});
export type MakeCurrentBody = z.infer<typeof MakeCurrentBody>;

/** Error codes of Make current (besides VERSION_CONFLICT and KIND_MISMATCH). */
export const MAKE_CURRENT_ERRORS = {
  ALREADY_CURRENT: 'ALREADY_CURRENT',
  IN_BIN: 'IN_BIN',
  PURGED: 'PURGED',
  NOT_READY: 'NOT_READY',
} as const;

/** Error codes of a version delete. */
export const VERSION_DELETE_ERRORS = {
  CURRENT_VERSION: 'CURRENT_VERSION',
  VERSION_IN_USE: 'VERSION_IN_USE',
  VERSION_DELETED: 'VERSION_DELETED',
} as const;

/** The Old versions scan's criteria (POST /api/v1/storage/old-versions/scan, and the cleanup job). */
export const OldVersionsCriteria = z.object({
  /** Keep the latest N versions of each asset (the current one is always kept). */
  keep: z.number().int().min(1).max(10).default(1),
  /** Only versions created more than D days ago. */
  olderThanDays: z.number().int().min(0).max(36500).default(30),
  /** Only assets of these classes (none: every class). */
  categoryIds: z.array(z.string().uuid()).max(50).default([]),
  /** Only versions with a file on this location. */
  locationId: z.string().uuid().nullable().default(null),
});
export type OldVersionsCriteria = z.infer<typeof OldVersionsCriteria>;

/** What a scan found. */
export interface OldVersionsScan {
  criteria: OldVersionsCriteria;
  /** Versions that would go to the bin, and their assets. */
  versions: number;
  assets: number;
  bytes: number;
  /** Per location: what PRISM manages there (freed when the bin purges them) and what is in place (only uncatalogued). */
  byLocation: Array<{ id: string; name: string; managedBytes: number; inPlaceBytes: number; files: number }>;
  /** The 50 largest. */
  largest: Array<{ assetId: string; title: string; versionId: string; number: number; sizeBytes: number; createdAt: string }>;
  /** Versions the criteria match that something uses, by reason, and a sample. */
  skipped: { versions: number; byReason: Partial<Record<VersionUseKind, number>>; sample: Array<{ assetId: string; title: string; number: number; use: VersionUse }> };
  /** The scan stopped at its time limit: the figures are at least these. */
  partial: boolean;
  /** When the bin purges what goes there now (null: the bin is off, the undo window applies). */
  purgeAfter: string | null;
  retentionDays: number | null;
}

/** POST /api/v1/storage/old-versions/cleanup: move what the criteria match to the bin, at most `confirmCount` versions. */
export const OldVersionsCleanupBody = z.object({
  criteria: OldVersionsCriteria,
  /** The number of versions, typed by the person as the confirmation (the scan's `versions`). */
  confirmCount: z.number().int().min(1),
});

export interface VersionCleanupJobDTO {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  criteria: OldVersionsCriteria;
  maxVersions: number;
  processed: number;
  deleted: number;
  skipped: number;
  deletedBytes: number;
  skippedReasons: Partial<Record<VersionUseKind | 'changed', number>>;
  error: string | null;
  requestedBy: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** When the bin purges what the job moved there. */
  purgeAfter: string | null;
}

/** "Textless and M&E", "Textless, M&E and Clean feed". */
export function andList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
